<template>
    <div class="custom-calendar">
        <div class="nav">
            <div
                class="arrow left"
                @click="left()"
            >
            </div>

            <div class="nav-label">
                <span v-if="activeDisplay===0"  @click="changeDisplay()">
                    {{ `${selectedYear} ${months[selectedMonth -1]}` }}
                </span>

                <span v-else-if="activeDisplay===1"  @click="changeDisplay()">
                    {{ `${selectedYear}` }}
                </span>

                <span v-else @click="changeDisplay()">
                    {{ `${selectedYear + activeYearRange} - ${(selectedYear + activeYearRange) + 15}` }}
                </span>
            </div>

            <div
                class="arrow right"
                @click="right()"
            >
            </div>
        </div>

        <div class="years" v-if="activeDisplay===2">
            <div
                class="year"
                :class="{'selected' : (selectedYear + (index + activeYearRange))===selectedYear}"
                v-for="(i,index) in 16"
                :key="index"
                @click="select('year', selectedYear + (index + activeYearRange))"
            >
                {{ selectedYear + (index + activeYearRange) }}
            </div>
        </div>

        <div class="months"  v-if="activeDisplay===1">
            <div
                class="month"
                v-for="(m,index) in months"
                :key="index"
                :class="{'selected' : m===months[selectedMonth-1]}"
                @click="select('month', index + 1)"
            >
                {{ m.substring(0,3) }}
            </div>
        </div>

		<div class="table-wrapper">
			<table class="days" v-if="activeDisplay===0">
				<thead>
					<tr>
						<th v-for="d in days" :key="d">
							{{ d }}
						</th>
					</tr>
				</thead>

				<tbody>
					<tr v-for="(w,index) in dates" :key="index">
						<td
							v-for="(d, index) in w"
							:key="index"
							:class="{
								'selected' : value
											&& d.value === value.getDate()
											&& (selectedMonth-1) === value.getMonth()
											&& selectedYear === value.getFullYear(),
								'today' : d.class === 'today',
								'disable' : d.disable || !d.value
							}"
							@click="changeDate(d.value, $event)"
						>
							<slot name="day" :day="d">
								{{ d.value }}
							</slot>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
    </div>
</template>

<script>
export default {
	props: {
		value: {
			type: Date,
			default: null
		}
	},

	data () {
		return {
			activeDisplay: 0,
			activeYearRange: 0,
			selectedYear: null,
			selectedMonth: null,
			selectedDate: null,
			days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
			months: [
				'January',
				'February',
				'March',
				'April',
				'May',
				'June',
				'July',
				'August',
				'September',
				'October',
				'November',
				'December'
			],

			dates: []
		}
	},

	computed: {
		today () {
			return new Date()
		},

		currentDate () {
			return this.today.getDate()
		},

		currentMonth () {
			return this.today.getMonth() + 1
		},

		currentYear () {
			return this.today.getFullYear()
		},

		selected () {
			let date = new Date()
			date.setDate(this.selectedDate)
			date.setMonth(this.selectedMonth - 1)
			date.setFullYear(this.selectedYear)
			return date
		}
	},

	watch: {
		value (val) {
			if (val) {
				this.selectedMonth = val.getMonth() + 1
				this.selectedYear = val.getFullYear()
				this.selectedDate = val.getDate()
			}
		}
	},

	mounted () {
		if (this.value) {
			this.selectedMonth = this.value.getMonth + 1
			this.selectedYear = this.value.getFullYear()
			this.selectedDate = this.value.getDate()
		} else {
			this.selectedMonth = this.currentMonth
			this.selectedYear = this.currentYear
			this.selectedDate = this.currentDate

			this.$emit('input', this.selected)
		}

		this.generateCalendar()
	},

	methods: {
		select (type, val) {
			if (type === 'month') {
				this.changeMonth(val)
				this.activeDisplay = 0
				return
			}

			if (type === 'year') {
				this.changeYear(val)
				this.activeDisplay = 1
			}
		},

		changeDisplay () {
			if (this.activeDisplay === 2) {
				this.activeDisplay = 0
				return
			}

			this.activeDisplay += 1
		},

		left () {
			if (this.activeDisplay === 0) {
				if (this.selectedMonth === 1) {
					this.changeYear(this.selectedYear - 1)
					this.changeMonth(12)
					return
				}

				this.changeMonth(this.selectedMonth - 1)
				return
			}

			if (this.activeDisplay === 1) {
				this.changeYear(this.selectedYear - 1)
				return
			}

			if (this.activeDisplay === 2) {
				if (this.activeYearRange === -32) {
					return
				}

				this.activeYearRange -= 16
			}
		},

		right () {
			if (this.activeDisplay === 0) {
				if (this.selectedMonth === 12) {
					this.changeYear(this.selectedYear + 1)
					this.changeMonth(1)
					return
				}

				this.changeMonth(this.selectedMonth + 1)
				return
			}

			if (this.activeDisplay === 1) {
				this.changeYear(this.selectedYear + 1)
				return
			}

			if (this.activeDisplay === 2) {
				if (this.activeYearRange === 32) {
					return
				}

				this.activeYearRange += 16
			}
		},

		toggle () {
			this.activeDisplay = 0
		},

		changeDate (val, event) {
			if (event.target.classList.contains('selected')) {
				this.$emit('click')
				return
			}

			this.selectedDate = val

			this.$emit('input', this.selected)
		},

		async changeYear (val) {
			this.selectedYear = val
			await this.generateCalendar()
			this.$emit('year-change', this.selected)
		},

		async changeMonth (val) {
			this.selectedMonth = val
			await this.generateCalendar()
			this.$emit('month-change', this.selected)
		},

		async generateCalendar () {
			this.dates = []

			let firstDay = (new Date(this.selectedYear, this.selectedMonth - 1)).getDay()

			let date = 1

			for (let i = 0; i < 5; i++) {
				this.dates.push(i)
				this.dates[i] = []

				for (let j = 0; j < 7; j++) {
					if (i === 0 && j < firstDay) {
						this.dates[i].push(j)
						this.dates[i][j] = { value: '', class: '' }
					} else if (date > this.getDaysInMonth(this.selectedMonth - 1, this.selectedYear)) {
						break
					} else {
						this.dates[i].push(j)
						this.dates[i][j] = { value: date, class: '' }

						if (date === this.currentDate && this.selectedYear === this.currentYear && this.selectedMonth === this.currentMonth) {
							this.dates[i][j].class = 'today'
						}

						date++
					}
				}
			}
		},

		getDaysInMonth (m, y) {
			return 32 - new Date(y, m, 32).getDate()
		}
	}
}
</script>

<style lang="scss" scoped>
.custom-calendar {
    background-color: #fff;
    border: 1px solid #D0D0DF;
    font-size: 14px;
    width: 100%;
    height: 100%;
	box-shadow: 0 3px 6px #d0d0df80;
	display: flex;
	flex-direction: column;

    .nav {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 10px;
        font-weight: 500;

        .nav-label {
            flex: 1 1 auto;
            display: inline-block;
            text-align: center;
            text-transform: uppercase;
            cursor: pointer;
			font-size: 14px;

			&:hover {
				color: #2DC4B6;
			}
        }

        .arrow {
            cursor: pointer;
            width: 10px;
            height: 10px;
            margin-left: 5px;
            border: solid #222625;
            border-width: 0 1px 1px 0;
            display: inline-block;

            &.left {
                transform: rotate(135deg);
            }

            &.right {
                transform: rotate(-45deg);
            }

            &:hover {
                border-color: #2DC4B6;
            }
        }
	}

	.table-wrapper {
		flex: 1 1 auto;
	}

	table {
		border-collapse: collapse;
		table-layout: fixed;
		width: 100%;
		height: 100%;

		thead {
			background-color: #F9F9F9;
			padding: 0 10px;

			th {
				padding: 5px;
				text-align: center;
				font-weight: 400;
				font-size: 12px;
				white-space: nowrap;
			}
		}

		th, td {
			border: 1px solid #D0D0DF;
		}

		tbody {
			td {
				text-align: center;
				font-size: 14px;
				padding: 5px;
				color: #222625;
				cursor: pointer;
			}
		}
	}

    .months, .years {
        display: flex;
        align-items: flex-start;
        justify-content: center;
        flex-wrap: wrap;
        height: 100%;

        .month, .year {
            flex: 1 1 25%;
            text-align: center;
            padding: 15px 10px;
            cursor: pointer;
            display: block;
        }
    }

    .month,
    .year,
    td {
        &.today,
        &.selected,
        &:hover {
            color: #2DC4B6;
        }

        &.disable {
            color: #ccc;
			pointer-events: none;
        }
    }
}
</style>
